import { html, css, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';

import NSAutoPaymentsCard from './card-base.js';
import JobSchema from '../../../../../models/job-schema.js';
import type Company from '../../../../../models/company.js';
import type Workflow from '../../../../../models/workflow.js';
import definition from '@job-schemas/public-ledger/auto-payments/stop.json';
import { jobSchema } from '../../../job-schema.js';
import { AUTOPAY_CARD_OUTCOMES } from './presentations.js';
import { createLogger } from '../../../../../ns-services/logger.js';
import { lambdaErrorCode } from '../../../../../helpers/lambda.js';
import { readingCredits } from '../../../../../helpers/public-ledger-credits.js';
import { AUTOPAY_CODES, cardDots } from '../../../../../helpers/auto-payments.js';
import { proposeStop } from '../../../../../helpers/auto-payments-api.js';
import { SIGNATORY_CHANGE_REASON } from '../../../../../helpers/auto-payment-changes.js';
import {
  STOP_BUTTON,
  STOP_CHANGE_OPEN_TEXT,
  STOP_INTRO,
  STOP_NOT_ACTIVE_TEXT,
  STOP_PROPOSED_TEXT,
  stopConsequences,
  stopDoneText,
  stopStage,
  type StopStage,
} from '../../../../../helpers/auto-payment-stop.js';

import '../../../../../ns-elements/ns-job-layout.js';

const logger = createLogger('ns-public-ledger-auto-payments-stop');

/**
 * Stop automatic payments (#40): held by the signatory who started it from My Account. It says what stopping means, with
 * the company's own figures (DECISION 1: credits in the company account stay there, unusable until automatic payments are
 * set up again), and asks the signatories to sign the stop (public-ledger/autopay/propose-stop, which completes the card
 * `proposed`). The stop is an allowance of 0 on the public ledger, paid by nodeStream; automatic payments stop once it has
 * run. Once the stop has ended the card says how (its workflow's outcome).
 */
@customElement(NSPublicLedgerAutoPaymentsStop.is)
export default class NSPublicLedgerAutoPaymentsStop extends NSAutoPaymentsCard {
  static get is() {
    return 'ns-public-ledger-auto-payments-stop';
  }

  static get schema(): JobSchema {
    // Every outcome is hidden: presentations.ts says whose each one is
    return jobSchema(definition, {
      outcomes: AUTOPAY_CARD_OUTCOMES['@ns/public-ledger-auto-payments-stop'],
      icon: 'bank-card',
    });
  }

  static styles = [
    ...super.styles,
    css`
      .consequences {
        margin: 0;
        padding-inline-start: var(--wa-space-l);
      }
    `,
  ];

  // The company: its balances for the figures, and whether a signatory change is pending
  @state()
  private __company: Company | null = null;

  // This card's stop, once propose-stop has recorded it
  @state()
  private __proposed: string | null = null;

  // The stop's workflow, once the job is done: its outcome says how the stop ended
  @state()
  private __workflow: (Workflow & { outcome?: string | null }) | null = null;

  async connectedCallback() {
    await super.connectedCallback();
    const job = this._job;
    if (!job) return;
    await this.__loadCompany();
    await this.__loadWorkflow();
    const workflowId = job.workflow?.id;
    if (workflowId) this._subscriptions.subscribe(`workflow.${workflowId}.*`, () => void this.__loadWorkflow());
  }

  private get __stage(): StopStage {
    return stopStage({
      completed: this._completed,
      claimed: this._claimed,
      row: this._row,
      personId: this._me,
      proposedTransactionId: this.__proposed,
      changePending: this.__company?.ledgerCred?.signatureRequirement?.awaitingUpdate === true,
      rowLoaded: this._rowLoaded,
    });
  }

  private async __loadCompany() {
    const companyId = this._job?.companyId;
    if (!companyId || !this._db) return;
    try {
      this.__company = ((await this._db.getById('company', companyId)) as Company | undefined) ?? null;
    } catch (err) {
      logger.warn('Unable to read the company', err);
    }
  }

  // Read once the job is done, and again on the workflow's updates
  private async __loadWorkflow() {
    const workflowId = this._job?.workflow?.id;
    if (!workflowId || !this._db || !this._completed) return;
    try {
      this.__workflow =
        ((await this._db.getById('workflow', workflowId)) as typeof this.__workflow | undefined) ?? null;
    } catch (err) {
      logger.warn('Unable to read the stop', err);
    }
  }

  private async __propose() {
    const job = this._job;
    if (!job) return;
    await this._serverAction(
      async () => {
        try {
          const { transactionId } = await proposeStop(this._auth?.token, job.id);
          this.__proposed = transactionId;
          return `${STOP_PROPOSED_TEXT} This job closes in a moment.`;
        } catch (err) {
          // A signatory change started meanwhile: the card says so once the company is read again
          if (lambdaErrorCode(err) === AUTOPAY_CODES.signatoryChange) await this.__loadCompany();
          throw err;
        }
      },
      'Asking the signatories to stop automatic payments',
      'The stop could not be proposed. Please try again.',
    );
  }

  private __renderConsequences(): TemplateResult {
    const balances = this.__company?.ledgerCred?.balances;
    const lines = stopConsequences({
      companyCredits: readingCredits(balances?.company),
      operatingCredits: readingCredits(balances?.operating),
      card: cardDots(this._row),
    });
    return html`
      <div class="job-panel">
        <h3>What stopping means</h3>
        <ul class="consequences">
          ${lines.map(
            (line) => html`
              <li>${line}</li>
            `,
          )}
        </ul>
      </div>
    `;
  }

  private __renderStage(): TemplateResult | typeof nothing {
    switch (this.__stage) {
      case 'DONE': {
        // No line while the stop's workflow can't be read: the card just closes
        const done = stopDoneText(this.__workflow);
        return done
          ? html`
              <div class="job-panel">
                <p>${done}</p>
              </div>
            `
          : nothing;
      }
      case 'LOADING':
        return nothing;
      case 'CLAIM':
        return this._renderClaimNote('stop automatic payments');
      case 'PROPOSED':
        return html`
          <div class="job-panel" role="status">
            <p>${STOP_PROPOSED_TEXT}</p>
          </div>
        `;
      case 'NOT_ACTIVE':
        return html`
          <div class="job-panel">
            <p>${STOP_NOT_ACTIVE_TEXT}</p>
          </div>
        `;
      case 'CHANGE_OPEN':
        return html`
          <div class="job-panel">
            <p>${STOP_CHANGE_OPEN_TEXT}</p>
          </div>
          ${this.__renderConsequences()}
        `;
      case 'LOCKED':
        return html`
          <div class="job-panel">
            <p>${SIGNATORY_CHANGE_REASON}.</p>
          </div>
          ${this.__renderConsequences()}
        `;
      default:
        return this.__renderConsequences();
    }
  }

  render() {
    return html`
      <ns-job-layout>
        <p slot="intro">${STOP_INTRO}</p>
        ${this._renderWaiting() ?? this.__renderStage()}
        <div slot="aside" class="primary-info">
          <span class="info-icon" aria-hidden="true">i</span>
          <h3>About stopping</h3>
          <p>
            Every signatory is asked to sign the stop on your company's public ledger account, where it records who
            stopped automatic payments. It removes the allowance automatic top-ups draw on, so the operating account is
            no longer topped up from the company account.
          </p>
          <p>nodeStream pays every public ledger fee for the stop.</p>
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    if (this._completed || this._waiting !== null) return nothing;
    const stage = this.__stage;
    if (stage === 'LOADING' || stage === 'PROPOSED') return nothing;
    if (!this._claimed) return this._renderClaimButton();
    const cancel = html`
      <wa-button
        variant="brand"
        appearance="outlined"
        type="button"
        ?disabled=${this._busy}
        @click=${() => this.outcome('cancel')}
      >
        Cancel
      </wa-button>
    `;
    if (stage !== 'READY') return cancel;
    return html`
      ${cancel}
      <wa-button variant="brand" type="button" ?disabled=${this._busy} @click=${() => this.__propose()}>
        ${STOP_BUTTON}
      </wa-button>
    `;
  }
}
